import type { ApiErrorModel } from '~/common/models/api.models.ts';
import type { UseCreditCardFormOptions } from './creditCardFormTypes.ts';
import type { PaymentIntentSnapshot } from '../interfaces/paymentIntentSnapshot.interface';
import { useNetwork } from '@vueuse/core';
import { usePaymentError } from './usePaymentError';
import { useHyperswitchRail } from './useHyperswitchRail';
import { useCardPaymentTiming } from './useCardPaymentTiming';
import { useCardPaymentIntent } from './useCardPaymentIntent';
import { useCardPaymentFailure } from './useCardPaymentFailure';
import { useCardPaymentAccept } from './useCardPaymentAccept';
import { useCardAuthorization } from './useCardAuthorization';
import { useWalletLoader } from './useWalletLoader';
import { isPaymentIntentSnapshotValid } from '../utils/paymentIntentSnapshot';
import { rememberPaymentMethod } from '../utils/paymentMethodStorage';

export function useCreditCardForm(options: UseCreditCardFormOptions) {
  const { $paymentMethods, $logger } = useNuxtApp();
  const { reportPaymentError } = usePaymentError();
  const store = useKegelAnalyticStore();
  const { openError } = useAllModals();
  const dialogStore = useDialogStore();
  const { gatewayType } = useHelpers();
  const { track } = useEventsDispatcher();
  const gatewayStore = useKegelGatewayStore();
  const { isOnline } = useNetwork();
  const isLago = computed(() => gatewayStore.provider === 'round');
  const isHyperswitch = useHyperswitchRail();

  const formState = useCardFormState({ withEmail: options.withEmail });
  const {
    paymentComponent,
    nameOnCard,
    isFormFilled,
    isValidEmail,
    validateEmailFn,
  } = formState;

  const timing = useCardPaymentTiming();
  const intent = useCardPaymentIntent({ ...options, isLago, nameOnCard });
  const failure = useCardPaymentFailure({ ...options, isLago, intent, timing });
  const { onAuthorizationSuccess } = useCardPaymentAccept({
    ...options,
    nameOnCard,
    intent,
    timing,
    onAcceptError: failure.onAcceptError,
  });
  const { authorize, isRedirecting } = useCardAuthorization({
    paymentIntent: intent.paymentIntent,
    paymentComponent,
    nameOnCard,
    isLago,
    isHyperswitch,
    onCardError: failure.onCardError,
  });
  const { onWalletClick, onWalletComplete, onWalletCancel } = useWalletLoader({
    onWalletFailure: error => failure.onCardError(null, error),
    onWalletAuthorized: options.onWalletAuthorized,
  });

  async function validate(): Promise<boolean> {
    store.isDisabledBaseDialog = true;

    if (options.withEmail && isValidEmail && !isValidEmail.value) {
      openError({ content: 'mail_error_message' });
    }

    if (!isFormFilled()) return false;

    try {
      return (await paymentComponent.value?.validateCardDetails?.()) !== false;
    } catch (error: unknown) {
      reportPaymentError({
        error: error as ApiErrorModel,
        errorCode: 'card_validation_threw',
        stage: options.upsale.value ? 'Upsale' : 'Subscription',
        type: $paymentMethods.card,
      });

      return true;
    }
  }

  async function passesPreflight(): Promise<boolean> {
    if (!isOnline.value) {
      openError({ content: 'no_internet_page_text_subtitle' });
      dialogStore.setLoader(false);

      return false;
    }

    if (options.withEmail && validateEmailFn) {
      const isUserAuthorized = await validateEmailFn(false);
      dialogStore.setLoader(true);

      if (!isUserAuthorized) {
        dialogStore.setLoader(false);

        return false;
      }
    }

    if (!(await validate())) {
      failure.rejectIncompleteForm();

      return false;
    }

    return true;
  }

  function rejectReplacedIntent(mountedIntentId: string | undefined): boolean {
    if (!isHyperswitch.value || intent.paymentIntent.value?.id === mountedIntentId) return false;

    failure.unlockForm();
    openError({ content: 'lp_v7_pop_up_payment_error_message' });
    $logger.error(new Error(
      `Hyperswitch intent replaced at pay time (${mountedIntentId} -> ${intent.paymentIntent.value?.id})`,
    ), { tags: { flow: 'payment', method: 'card', step: 'confirm' } });

    return true;
  }

  async function settleAuthorization(authorizationSnapshot: PaymentIntentSnapshot | null): Promise<void> {
    timing.finishAuthorization();

    if (!intent.paymentIntent.value?.status || failure.is3dsError.value) {
      failure.unlockForm();

      return;
    }

    if (!isPaymentIntentSnapshotValid(authorizationSnapshot, intent.currentSnapshot())) {
      throw new Error(`Card payment intent changed before accept for ${options.roundedPrice.value}`);
    }

    await onAuthorizationSuccess();
  }

  async function handlePayment(): Promise<void> {
    dialogStore.setLoader(true);
    failure.is3dsError.value = false;

    if (!(await passesPreflight())) return;

    try {
      timing.startAuthorization();

      const mountedIntentId = intent.paymentIntent.value?.id;
      await intent.refresh();

      if (rejectReplacedIntent(mountedIntentId)) return;

      track('Authorization_Start', {
        type: $paymentMethods.card,
        gateway: gatewayType.value,
      });
      rememberPaymentMethod('card');

      const authorizationSnapshot = intent.snapshot.value
        ? { ...intent.snapshot.value }
        : null;

      await authorize();

      if (isRedirecting.value) return;

      await settleAuthorization(authorizationSnapshot);
    } catch (error: unknown) {
      await failure.handleAuthorizationError(error);
    }
  }

  watch(paymentComponent, (component) => {
    if (!component || isLago.value) return;

    void (async () => {
      const chargebee = (globalThis as any).Chargebee?.getInstance?.();

      await chargebee?.load?.('3ds-handler');
    })().catch(() => undefined);
  }, { immediate: true });

  return {
    ...formState,
    handlePayment,
    onWalletClick,
    onWalletComplete,
    onWalletCancel,
    dialogStore,
  };
}
